import { OrganizationsActions } from "../../Organizations/_actions/OrganizationsActions";
import { OrganizationsSelector } from "../../Organizations/_reducers/organizations.reducer";
import { Organization } from "../../Organizations/_models/Organization";
import { PMSActiveOrganizationStore } from "../_stores/PMSActiveOrganizationStore";
import { PMSScopeOverrideStore } from "../_stores/PMSScopeOverrideStore";
import { PMSLocationsSelector } from "../_reducers/locations.reducer";

// The PMS is organization-centric: when the user belongs to an organization,
// Customization and Integrations operate on it (personal templates are the
// legacy fallback for users without one). With several organizations the
// explicit choice (PMSActiveOrganizationStore) wins; otherwise the first one.
// A screen working in a scope of its own (a location's integration setup)
// overrides all of that through PMSScopeOverrideStore, personal scope
// included -- which the active organization alone can never express.
export function resolveActiveOrganization(state: any): Organization | null {
    const organizations: Organization[] =
        OrganizationsSelector.getOrganizations(state) ?? [];
    const override = PMSScopeOverrideStore.selectorGet(state);
    if (override) {
        return (
            organizations.find(
                (organization) => organization.uid === override.organizationUid,
            ) ?? null
        );
    }
    const choiceUid = PMSActiveOrganizationStore.selectorGet(state);

    return (
        organizations.find((organization) => organization.uid === choiceUid) ??
        organizations[0] ??
        null
    );
}

// Guarantees the organizations list has been fetched before resolving the
// active one, so the scope does not depend on which screen the user visited
// first. Returns false when it could not be loaded (network/API failure):
// callers must NOT fall back to the personal scope in that case, since the
// user may actually belong to an organization.
export async function ensureOrganizationsLoaded(
    dispatch: any,
    getState: any,
): Promise<boolean> {
    if (OrganizationsSelector.getHasLoaded(getState())) return true;
    await dispatch(OrganizationsActions.listOrganizations());
    return OrganizationsSelector.getHasLoaded(getState());
}

// Whose PMS accounts apply to the open location. The LOCATION decides, not
// the session: the backend links an organization's template only to
// locations of that organization (error 790), and on a location with NO
// organization (null) such a link is accepted but not recorded -- an
// integration with no account -- while a personal one sticks. So: its
// organization when the user is a member, and the personal scope (undefined)
// when it has none or the user is not a member -- an ADMIN by invitation on
// another organization's location has no templates of it to ask for, and the
// backend keeps that user's rows personal (it refuses personal ones, 793,
// only to members). `undefined` from the location listing is a payload
// without the field yet, kept on the active scope rather than silently
// dropped to the personal one.
export function resolveLocationOrganizationUid(state: any): string | undefined {
    const locationOrganizationUid =
        PMSLocationsSelector.getPMSLocationSelectedOrganizationUid(state);
    if (locationOrganizationUid === undefined)
        return resolveActiveOrganization(state)?.uid;
    if (locationOrganizationUid === null) return undefined;
    const organizations: Organization[] =
        OrganizationsSelector.getOrganizations(state) ?? [];
    const isMember = organizations.some(
        (organization) => organization.uid === locationOrganizationUid,
    );
    return isMember ? locationOrganizationUid : undefined;
}

// Whether the open location's accounts are the user's personal ones
// (resolveLocationOrganizationUid). Those have no screen of their own once the
// user has organizations -- the standalone module works in the active one --
// so the location is where they are looked at. Not before the organizations
// are in: a member would pass for an outsider meanwhile.
export function isLocationInPersonalScope(state: any): boolean {
    if (PMSLocationsSelector.getPMSLocationSelectedOrganizationUid(state) === null)
        return true;
    return (
        OrganizationsSelector.getHasLoaded(state) &&
        resolveLocationOrganizationUid(state) === undefined
    );
}
